import { ReactNode } from "react";

interface DashboardSectionProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Optional count shown as a badge next to the title (e.g. number of items). */
  count?: number;
  /** Optional action rendered top-right (e.g. a "mark all read" button). */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * Generic container for a dashboard module. Keeps the notifications dashboard
 * modular: each concern (outstanding communication, notifications, …) renders
 * inside its own section with a consistent header.
 */
const DashboardSection = ({
  title,
  description,
  icon,
  count,
  action,
  children,
}: DashboardSectionProps) => {
  return (
    <section className="rounded-lg border border-neutral-primary/10 bg-white shadow-sm">
      <header className="flex items-start justify-between gap-3 border-b border-neutral-primary/10 p-4">
        <div className="flex items-start gap-3">
          {icon && <div className="mt-0.5 text-moss-primary">{icon}</div>}
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium text-neutral-primary">{title}</h2>
              {typeof count === "number" && count > 0 && (
                <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-moss-primary px-1.5 text-xs font-medium text-white">
                  {count}
                </span>
              )}
            </div>
            {description && (
              <p className="mt-0.5 text-sm text-neutral-secondary-mid">{description}</p>
            )}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
};

export default DashboardSection;
